import useSWR from 'swr';
import { PolicyExtended, PolicySlim } from 'utility/node/policy/policy.model';
import { getPoliciesRoute, getPolicyRoute } from 'utility/node/routes';
import { Status } from 'utility/node/tia-rest/model/response/policy.response.model';
import { fetcherWithoutWrapper } from 'utility/api';

const activeQuoteStatuses = [Status.QUOTE, Status.SUSPENDED_QUOTE];
const canceledQuoteStatuses = [Status.CANCELED_QUOTE];
const activePolicyStatuses = [
  Status.ACTIVE,
  Status.LATER_IN_FORCE,
  Status.AWAITING_INPUT,
];
const expiredPolicyStatuses = [Status.EXPIRED];

const defaults: Pick<PoliciesOptions, 'fetch' | 'revalidateOnMount'> = {
  fetch: true,
  revalidateOnMount: true,
};

interface PoliciesOptions {
  fetch?: boolean;
  revalidateOnMount?: boolean;
}

interface PolicyExtendedBatch {
  content: PolicyExtended;
}

export const usePolicies = (
  coverDate?: Date,
  options?: PoliciesOptions,
  notIncludeConsentedParties?: boolean,
) => {
  const _options = { ...defaults, ...options };

  const { fetch, revalidateOnMount } = _options;

  const { data, error, mutate, isLoading } = useSWR<PolicySlim[]>(
    fetch ? getPoliciesRoute(coverDate, notIncludeConsentedParties) : null,
    () => getPolicies(coverDate, notIncludeConsentedParties),
    {
      revalidateOnMount: revalidateOnMount,
    },
  );
  const filteredPolicies = getFilteredResponseData(data);

  return {
    ...filteredPolicies,
    policiesErrors: error,
    policiesLoading: !data && !error && fetch && isLoading,
    mutatePolicies: mutate,
  };
};

const getPolicies = async (
  coverDate: Date,
  notIncludeConsentedParties: boolean,
) =>
  await fetcherWithoutWrapper(
    getPoliciesRoute(coverDate, notIncludeConsentedParties),
    {},
  ).then((r) => r);

const getFilteredResponseData = (data: PolicySlim[]) => {
  const quotes = data?.filter((p) => activeQuoteStatuses.includes(p.status));
  let firstQuoteToExpire: PolicySlim | null = null;
  const sortedQuotes = quotes?.sort((a, b) => {
    if (!b.quoteExpirationInDays) {
      return -1;
    }
    return a.quoteExpirationInDays - b.quoteExpirationInDays;
  });

  if (!!sortedQuotes && sortedQuotes.length > 0) {
    firstQuoteToExpire = sortedQuotes[0];
  }

  const activePolicies = data?.filter((p) =>
    activePolicyStatuses.includes(p.status),
  );
  const expiredPolicies = data?.filter((p) =>
    expiredPolicyStatuses.includes(p.status),
  );
  const canceledQuotes = data?.filter((p) =>
    canceledQuoteStatuses.includes(p.status),
  );

  return {
    policies: data,
    activePolicies,
    expiredPolicies,
    activeQuotes: quotes,
    canceledQuotes,
    firstQuoteToExpire: firstQuoteToExpire,
  };
};

export const useExtendedPolicies = (
  coverDate?: Date,
  options?: PoliciesOptions,
  notIncludeConsentedParties?: boolean,
) => {
  const _options = { ...defaults, ...options };

  const { fetch, revalidateOnMount } = _options;

  const { data, error, isLoading } = useSWR<PolicySlim[]>(
    fetch ? getPoliciesRoute(coverDate, notIncludeConsentedParties) : null,
    () => getPolicies(coverDate, notIncludeConsentedParties),
    {
      revalidateOnMount: revalidateOnMount,
    },
  );
  const filteredPolicies = getFilteredResponseData(data);

  const {
    data: extendedPolicies,
    error: extendedPoliciesError,
    isLoading: isExtendedPoliciesLoading,
  } = useSWR<PolicyExtendedBatch[]>(
    () =>
      filteredPolicies.activePolicies?.map((policy) =>
        getPolicyRoute(policy.policyNo),
      ),
    fetcher,
  );

  const filteredExtendedPolicies = extendedPolicies
    ?.map((policy) => policy.content)
    .filter((p) => !!p);

  return {
    ...filteredPolicies,
    extendedPolicies: filteredExtendedPolicies,
    policiesLoading: isLoading || isExtendedPoliciesLoading,
    policiesErrors: error || extendedPoliciesError,
  };
};

const fetcher = (urls: string[]): Promise<PolicyExtendedBatch[]> =>
  Promise.all(urls.map((url) => fetch(url).then((res) => res.json())));
